import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookText,
  BrainCircuit,
  FileStack,
  LayoutDashboard,
  LineChart,
  LogOut,
  Menu,
  Palette,
  Settings,
  Sparkles,
  SquareCheckBig,
  Users,
  X,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { apiRequest } from '../../lib/api';
import { BRAND } from '../../data/mock';
import AdminPanelSkeleton from './AdminPanelSkeleton';
import AdminSectionPlaceholder from './AdminSectionPlaceholder';
import '../../styles/adminCockpit.css';

const AdminBlogManager = lazy(() => import('./AdminBlogManager'));
const AdminCommandCenterPanel = lazy(() => import('./AdminCommandCenterPanel'));
const AdminLeadIntelligencePanel = lazy(() => import('./AdminLeadIntelligencePanel'));
const AdminLeadManagement = lazy(() => import('./AdminLeadManagement'));
const AdminSettingsPanel = lazy(() => import('./AdminSettingsPanel'));
const AdminCRMPanel = lazy(() => import('./AdminCRMPanel'));
const AdminTasksPanel = lazy(() => import('./AdminTasksPanel'));
const AdminReportsPanel = lazy(() => import('./AdminReportsPanel'));
const AdminDocumentsPanel = lazy(() => import('./AdminDocumentsPanel'));
const AIBrandKitDesigner = lazy(() => import('./AIBrandKitDesigner'));
const AIContractGenerator = lazy(() => import('./AIContractGenerator'));

const suiteSections = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Executive Overview, Today’s Focus, AI Insights and Pipeline Snapshot.',
    subviews: [
      { id: 'executive-overview', label: 'Executive Overview', description: 'Command center with priorities, forecast, quick actions and key signals.' },
      { id: 'today-focus', label: 'Today’s Focus', description: 'What needs immediate attention, overdue follow-ups and today’s actions.' },
      { id: 'ai-insights', label: 'AI Insights', description: 'Opportunities and risks detected by the system.' },
      { id: 'pipeline-snapshot', label: 'Pipeline Snapshot', description: 'Pipeline health, forecast and hot leads.' },
    ],
  },
  {
    id: 'leads',
    label: 'Leads',
    icon: Users,
    description: 'Visual pipeline, leads list and profile as the single source of truth.',
    subviews: [
      { id: 'leads-board', label: 'Leads Board', description: 'Visual pipeline with drag-and-drop and operational cards.' },
      { id: 'leads-list', label: 'Leads List', description: 'Table view to review and edit incoming leads.' },
      { id: 'lead-profile', label: 'Lead Profile', description: 'Reserved space for the full lead page.' },
    ],
  },
  {
    id: 'tasks',
    label: 'Tasks',
    icon: SquareCheckBig,
    description: 'Dedicated center for follow-ups, reminders and next actions.',
    subviews: [
      { id: 'today', label: 'Today', description: 'Tasks and follow-ups due today.' },
      { id: 'overdue', label: 'Overdue', description: 'Overdue actions that need immediate rescue.' },
      { id: 'upcoming', label: 'Upcoming', description: 'Upcoming tasks and reminders.' },
      { id: 'completed', label: 'Completed', description: 'History of completed tasks.' },
    ],
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    icon: BrainCircuit,
    description: 'Parsing Center, queue, imports and AI analysis log.',
    subviews: [
      { id: 'parsing-queue', label: 'Parsing Queue', description: 'Parsing center and CRM approval.' },
      { id: 'imports', label: 'Imports', description: 'Reserved space for recent imports.' },
      { id: 'ai-analysis-log', label: 'AI Analysis Log', description: 'Consolidated log of analyses and results.' },
      { id: 'reanalysis-history', label: 'Reanalysis History', description: 'History of re-analyses and reviews.' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: LineChart,
    description: 'Pipeline, revenue, source and loss analytics.',
    subviews: [
      { id: 'pipeline-analytics', label: 'Pipeline Analytics', description: 'Pipeline analytics view.' },
      { id: 'revenue-analytics', label: 'Revenue Analytics', description: 'Revenue and forecast view.' },
      { id: 'source-analytics', label: 'Source Analytics', description: 'Source performance view.' },
      { id: 'loss-analysis', label: 'Loss Analysis', description: 'Loss and objections view.' },
    ],
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: FileStack,
    description: 'Proposals, contracts and operational upload center.',
    subviews: [
      { id: 'proposals', label: 'Proposals', description: 'Reserved space for proposals.' },
      { id: 'contracts', label: 'Contracts', description: 'Reserved space for contracts.' },
      { id: 'contract-generator', label: 'Contract Generator', description: 'Generate contracts with AI.' },
      { id: 'upload-center', label: 'Upload Center', description: 'Reserved space for uploads and document organization.' },
    ],
  },
  {
    id: 'brand-kit',
    label: 'Brand Kit',
    icon: Palette,
    description: 'AI brand designer.',
    subviews: [
      { id: 'brand-designer', label: 'Brand Designer', description: 'Create a complete brand kit with AI.' },
    ],
  },
  {
    id: 'blog',
    label: 'Blog Studio',
    icon: BookText,
    description: 'Drafts, published, content ideas and templates.',
    subviews: [
      { id: 'drafts', label: 'Drafts', description: 'Content in preparation and drafts.' },
      { id: 'published', label: 'Published', description: 'Published and distributed content.' },
      { id: 'content-ideas', label: 'Content Ideas', description: 'Reserved space for editorial ideas.' },
      { id: 'templates', label: 'Templates', description: 'Templates and reusable pieces.' },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: Settings,
    description: 'Pipeline Settings, AI Settings, Integrations, Team and Branding.',
    subviews: [
      { id: 'pipeline-settings', label: 'Pipeline Settings', description: 'Pipeline preferences and operational rules.' },
      { id: 'ai-settings', label: 'AI Settings', description: 'Models, keys and AI behavior.' },
      { id: 'integrations', label: 'Integrations', description: 'Stack connections and tools.' },
      { id: 'team-permissions', label: 'Team / Permissions', description: 'Reserved structure for permissions and ownership.' },
      { id: 'branding', label: 'Branding', description: 'Visual configuration and brand messaging.' },
    ],
  },
];

const getDefaultSubview = (sectionId) => suiteSections.find((section) => section.id === sectionId)?.subviews?.[0]?.id || '';

const AdminDashboard = () => {
  const { logout, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [activeSubview, setActiveSubview] = useState(getDefaultSubview('dashboard'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const refreshStats = useCallback(async () => {
    await apiRequest('/api/admin/stats', {
      headers: { Authorization: `Bearer ${token}` },
    });
  }, [token]);

  useEffect(() => {
    refreshStats()
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [refreshStats]);

  const activeSectionMeta = useMemo(() => suiteSections.find((section) => section.id === activeSection) || suiteSections[0], [activeSection]);
  const activeSubviewMeta = useMemo(
    () => activeSectionMeta.subviews.find((subview) => subview.id === activeSubview) || activeSectionMeta.subviews[0],
    [activeSectionMeta, activeSubview],
  );

  const handleNavigate = useCallback((sectionId, subviewId = null) => {
    setActiveSection(sectionId);
    setActiveSubview(subviewId || getDefaultSubview(sectionId));
    setMobileMenuOpen(false);
  }, []);

  const renderActivePanel = () => {
    if (activeSection === 'dashboard') {
      return <AdminCommandCenterPanel token={token} onRefreshStats={refreshStats} onNavigate={handleNavigate} view={activeSubview} />;
    }

    if (activeSection === 'leads') {
      if (activeSubview === 'leads-board') return <AdminCRMPanel token={token} />;
      if (activeSubview === 'leads-list') return <AdminLeadManagement token={token} onRefreshStats={refreshStats} />;
      return (
        <AdminSectionPlaceholder
          title="Lead Profile"
          description="This space is reserved for the future full lead page. For now, the premium profile is available from board/list."
          sections={['Overview', 'Contact / Business Info', 'Opportunity Details', 'Checklist / Tasks', 'Activity Timeline', 'AI Log', 'Growth Opportunities']}
          note="Meanwhile, the current detailed profile is available when opening a lead from Leads Board or Leads List."
        />
      );
    }

    if (activeSection === 'tasks') {
      return <AdminTasksPanel token={token} view={activeSubview} />;
    }

    if (activeSection === 'intelligence') {
      if (activeSubview === 'parsing-queue') return <AdminLeadIntelligencePanel token={token} onNavigate={handleNavigate} />;
      return (
        <AdminSectionPlaceholder
          title={activeSubviewMeta.label}
          description={activeSubviewMeta.description}
          sections={['Recent imports', 'Queue status', 'Confidence / missing info', 'Review queue']}
          note="The Parsing Center foundation lives in Parsing Queue. These views are ready for the next phase without breaking the current flow."
        />
      );
    }

    if (activeSection === 'reports') {
      return <AdminReportsPanel token={token} view={activeSubview} />;
    }

    if (activeSection === 'documents') {
      if (activeSubview === 'contract-generator') {
        return <AIContractGenerator token={token} />;
      }
      return <AdminDocumentsPanel token={token} view={activeSubview} />;
    }

    if (activeSection === 'brand-kit') {
      return <AIBrandKitDesigner token={token} />;
    }

    if (activeSection === 'blog') {
      return <AdminBlogManager token={token} onRefreshStats={refreshStats} view={activeSubview} />;
    }

    return <AdminSettingsPanel token={token} onRefreshStats={refreshStats} view={activeSubview} />;
  };

  if (loading) {
    return <div className="loading-screen"><div className="loader"></div></div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="container">
          <div className="admin-topbar" data-testid="admin-topbar">
            <div className="admin-topbar-left">
              <div className="admin-suite-branding compact" data-testid="admin-suite-branding">
                <div className="admin-suite-logo-wrap compact" data-testid="admin-suite-logo-wrap">
                  <img src={BRAND.logoUrl} alt={BRAND.name} loading="lazy" decoding="async" />
                </div>
                <div>
                  <span className="section-eyebrow">IllumiDev Ops Suite</span>
                  <h1 data-testid="admin-dashboard-title">{activeSectionMeta.label}</h1>
                </div>
              </div>
              <p className="admin-topbar-description" data-testid="admin-topbar-description">{activeSubviewMeta.description}</p>
            </div>
            <div className="admin-topbar-actions">
              <span className="admin-topbar-status" data-testid="admin-topbar-status">Private workspace</span>
              <button type="button" className="btn btn-outline admin-mobile-menu-button" onClick={() => setMobileMenuOpen(true)} data-testid="admin-mobile-menu-button">
                <Menu size={18} /> Menu
              </button>
              <button onClick={logout} className="btn btn-secondary" data-testid="admin-logout-button">
                <LogOut size={18} />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container ops-shell">
        <div className={`ops-mobile-backdrop ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(false)} />
        <div className={`ops-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`} data-testid="admin-sidebar">
          <div className="ops-sidebar-brand" data-testid="admin-sidebar-brand">
            <img src={BRAND.logoUrl} alt={BRAND.name} loading="lazy" decoding="async" />
            <div>
              <strong>IllumiDev</strong>
              <span>Admin suite</span>
            </div>
            <button type="button" className="ops-sidebar-close mobile-only" onClick={() => setMobileMenuOpen(false)} data-testid="admin-mobile-menu-close-button">
              <X size={18} />
            </button>
          </div>

          {suiteSections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.id}
                className={`ops-tab ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => handleNavigate(section.id)}
                data-testid={`admin-tab-${section.id}`}
              >
                <Icon size={18} />
                <span>{section.label}</span>
              </button>
            );
          })}

          <div className="ops-highlight-card" data-testid="admin-highlight-card">
            <Sparkles size={18} />
            <strong>Suite structure live</strong>
            <p>Dashboard, Leads, Tasks, Intelligence, Reports, Documents, Blog Studio and Settings are organized as product architecture.</p>
          </div>
        </div>

        <div className="ops-content">
          <div className="suite-subnav" data-testid="admin-suite-subnav">
            {activeSectionMeta.subviews.map((subview) => (
              <button
                key={subview.id}
                type="button"
                className={`suite-subnav-chip ${activeSubview === subview.id ? 'active' : ''}`}
                onClick={() => handleNavigate(activeSectionMeta.id, subview.id)}
                data-testid={`admin-subtab-${activeSectionMeta.id}-${subview.id}`}
              >
                {subview.label}
              </button>
            ))}
          </div>

          <Suspense fallback={<AdminPanelSkeleton />}>
            {renderActivePanel()}
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;